// lib/overlay/overlay-state.ts
//
// Pure domain layer for the app-shell overlay FSM (hex: domain vs delivery
// split, .tondocs/skills/hex.md) — zero React imports, unit-testable without
// a renderer. `reduceOverlay` is the ONLY place that decides overlay
// transitions; every consumer (context, components) imports `OverlayState`
// from here instead of declaring a parallel shape.
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §C.1 (type layer this implements)
// @see .tondocs/skills/type-driven-design.md (discriminated unions, `never`-probe)
// @see .tondocs/skills/react-state-design.md §4 (FSM via discriminated union)
//
// Two-slot amendment (2026-07-23, operator Q: "cart must open above details
// above search"): the original single-slot `AppOverlay` draft is superseded
// by `OverlayState = { panel, utility }`. `panel` = large slide-in surfaces
// (profile, chat, discover-category, creation, feed-details), max one.
// `utility` = small commerce/service sheets (cart, auction-bid), max one,
// always rendered ABOVE the panel (z-scale §C.3 — `overlayNested`). Legal
// stacking example: cart open over feed-details over `/search` =
// `{ panel: { kind: "feed-details", listingId }, utility: { kind: "cart" } }`.
//
// Phase 1 complete (§E step 1.8, 2026-07-23): every mechanism in §B.1 now
// dispatches through this reducer via `components/context/overlay-context.tsx`
// (wired in app/(site)/layout.tsx). The temporary migration mirror
// (`overlay-legacy-bridge.tsx`, escape-hatch shim described in §C.2) has been
// deleted — its last branch (feed-details) was retired in step 1.5, and steps
// 1.6a/1.6b/1.7 needed no bridge branch of their own (single-mount hoists and
// a pure-derivation refactor, not legacy-channel mirroring).

import { unreachable } from "@/lib/type-helpers";
import type { DiscoverCategorySelection } from "@/components/feed/feed-discover-category-slider";

/**
 * Large slide-in surfaces. Max one open at a time — opening a new panel
 * replaces the current one (§C.1 policy).
 *
 * No "search" kind: search is a route-owned *surface* (§C.0), not an
 * overlay — it must survive the listing-detail round-trip (back-nav lands
 * back on search with query/results/scroll intact), which an overlay's
 * transient slot state cannot provide. Surfaces are enumerated by routes.
 */
export type PanelOverlay =
  | { kind: "none" }
  | { kind: "profile" }
  | { kind: "profile-edit" } // mobile-only inline "edit my profile" mode — migrated step 1.7 (DONE 2026-07-23):
  // was `dataset.profileEditOpen` + a MutationObserver in bottom-nav-bar-client.tsx (mechanism 4, §B.1). Owned by
  // components/profile-page/profile-container.tsx: swaps ProfileTabs for ProfileEditForm IN PLACE on the visited
  // /[username] page (no route change, no dialog/backdrop) — same "in-place content overlay, needs nav-hide"
  // shape as feed-details below, so it qualifies under §C.1's inclusion criterion the same way. NOT the
  // `/profile/edit` ROUTE (app/(site)/profile/edit) — that page stays pathname-owned, untouched by this kind.
  | { kind: "chat"; roomId?: string }
  | { kind: "discover-category"; selection: DiscoverCategorySelection | null }
  | { kind: "creation"; draftId?: string } // draftId = resume a saved listing draft (My Listings → Drafts);
  // absent = fresh flow. Same "small identifier payload" convention as chat.roomId / feed-details.listingId.
  | { kind: "feed-details"; listingId: string }; // snap-feed in-place details — migrated step 1.5 (DONE 2026-07-23):
  // this ONE kind covers both the mobile bottom sheet (marketplace-vertical-listing-card.tsx) and the desktop
  // 420px side panel (marketplace-vertical-snap-feed.tsx). The type can't distinguish which presentation opened
  // it (by design — one canonical kind, not two); consumers that must (e.g. bottom-nav-bar-client.tsx, which
  // hid only for the mobile sheet pre-migration) combine `usePanelIs("feed-details")` with the same `useMobile()`
  // breakpoint hook the two producers already use, rather than the FSM growing a presentation axis.
// future (§F, post re-litigation of the 2026-07-16 route-modal rejection): { kind: "listing-detail" }

/**
 * Small commerce/service sheets. Max one open at a time, always rendered
 * ABOVE the panel slot (z-scale §C.3 `overlayNested`) — independent of
 * whatever panel (if any) is open underneath.
 */
export type UtilityOverlay =
  | { kind: "none" }
  | {
      kind: "cart";
      intent?: "browse" | "checkout"; // absorbs open-cart-checkout event (resolved step 1.3: components/cart/cart.tsx
      //                                  drives auto-select-single-seller + gotoCheckout off this, replacing the
      //                                  dead `openCartAndCheckout` window-event listener)
      forceRefresh?: boolean; // absorbs open-cart-force (step 1.3): bypasses the 60s `cart_last_load` localStorage
      //                         cache and skips the availability-refresh-key bump — "See in Cart" right after
      //                         Add to Cart already synced availability via the add mutation itself, so a second
      //                         forced round-trip only needs fresh cart *rows*, not a fresh availability check.
    }
  | {
      kind: "auction-bid";
      listingId: string;
      description?: string | null; // migrated step 1.4 (lib/auction-quick-bid-sheet-event.ts, deleted this
      //                               step): the event's optional prefetched description, shown by
      //                               GlobalAuctionBidSheet as a fallback until its own API fetch
      //                               (GET /api/listings/[id]/auction-quick-bid) returns a description.
    }; // future utility kinds: auth prompt, share

/**
 * Two independent typed slots — NOT one flat union. Mutual exclusion by
 * construction within each slot (one panel, one utility); the cross-slot
 * combination is exactly what enables the operator's stacking requirement
 * (utility ABOVE panel) while keeping illegal states (two panels, two
 * carts) unrepresentable.
 */
export type OverlayState = { panel: PanelOverlay; utility: UtilityOverlay };

export const INITIAL_OVERLAY_STATE: OverlayState = {
  panel: { kind: "none" },
  utility: { kind: "none" },
};

/**
 * v1 has no cross-slot stacking beyond panel+utility, no history stack
 * (§C.4 — browser history is the only SoT). Nested dialogs opened from
 * inside an overlay stay internal to that overlay's own component tree.
 */
export type OverlayAction =
  | { type: "openPanel"; panel: Exclude<PanelOverlay, { kind: "none" }> }
  | { type: "updatePanel"; panel: Exclude<PanelOverlay, { kind: "none" }> }
  | { type: "openUtility"; utility: Exclude<UtilityOverlay, { kind: "none" }> }
  | { type: "close" }
  | { type: "closePanel" }
  | { type: "closeUtility" };

const NONE_PANEL: PanelOverlay = { kind: "none" };
const NONE_UTILITY: UtilityOverlay = { kind: "none" };

/**
 * Pure transition function — `(state, action) => state`. No side effects,
 * no DOM, no React. Unit tests exercise this directly (tests/unit).
 *
 * Policy (§C.1, encoded here — not left to callers):
 *   - open within a slot = replace.
 *   - opening a panel closes an open utility (a new panel context
 *     invalidates the sheet stacked above it).
 *   - opening a utility does NOT touch the panel — the two slots are
 *     otherwise independent (cart can open with no panel open at all).
 *   - `updatePanel()` replaces the panel's PAYLOAD (e.g. `feed-details`'
 *     `listingId` following scroll) WITHOUT touching the utility slot, and
 *     ONLY when a panel of the SAME `kind` is already open — otherwise a
 *     no-op, returning `state` unchanged. This is deliberately distinct from
 *     `openPanel`: `openPanel` means "a new panel CONTEXT is being entered"
 *     (user-initiated, invalidates whatever utility sheet was stacked above
 *     the OLD panel) — `updatePanel` means "the ALREADY-open panel's own
 *     content drifted" (e.g. the active listing changed under an
 *     already-open feed-details panel as the user scrolls), which is not a
 *     new context and must NOT cascade-close the utility slot. Guarding on
 *     same-`kind` makes "update a closed/different panel" an invalid intent
 *     by construction — a caller with a stale reference to a panel that was
 *     since closed or replaced by something else silently no-ops instead of
 *     resurrecting or overwriting whatever is actually open now.
 *   - `close()` closes the topmost non-none slot: utility first, then
 *     panel. No-op identity when both slots are already none.
 *   - `closePanel()` / `closeUtility()` reset exactly that slot and leave
 *     the other slot untouched (no cascade) — a closed panel does not
 *     force-close a utility that happens to be open above it. This isn't
 *     spelled out explicitly in §C.1 (only `close()`'s ordering is); see
 *     the Phase-0-amendment handoff note for the reasoning.
 */
export function reduceOverlay(state: OverlayState, action: OverlayAction): OverlayState {
  switch (action.type) {
    case "openPanel":
      return { panel: action.panel, utility: NONE_UTILITY };
    case "updatePanel":
      return state.panel.kind === action.panel.kind ? { ...state, panel: action.panel } : state;
    case "openUtility":
      return { ...state, utility: action.utility };
    case "close":
      if (state.utility.kind !== "none") {
        return { ...state, utility: NONE_UTILITY };
      }
      if (state.panel.kind !== "none") {
        return { ...state, panel: NONE_PANEL };
      }
      return state;
    case "closePanel":
      return state.panel.kind === "none" ? state : { ...state, panel: NONE_PANEL };
    case "closeUtility":
      return state.utility.kind === "none" ? state : { ...state, utility: NONE_UTILITY };
    default:
      return unreachable(action);
  }
}
